import React, { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import KwikMemoriesLogo from '../../Assets/kwikmediaLogo.webp';
import HeroPhone from '../../Assets/Herosection/herosection_mobile.webp';
import HeroQr from '../../Assets/Herosection/qrcode.webp';
import user1 from '../../Assets/testimonial/twooo.webp';
import user2 from '../../Assets/testimonial/BollywoodStudio.webp';
import user3 from '../../Assets/testimonial/DhiyoyoStudio.webp';
import user4 from '../../Assets/testimonial/oneee.webp';
import user5 from '../../Assets/testimonial/AshokPhotography.webp';
import AndroidIcon from '../../Assets/androidIcon.png';
import AppleIcon from '../../Assets/appleIcon.png';

import { trackEvent } from '../../app/Firebase';

gsap.registerPlugin(ScrollTrigger);

// ── iOS Safari fix: normalize scroll behaviour ────────────────────────────────
ScrollTrigger.normalizeScroll(true);
ScrollTrigger.config({ ignoreMobileResize: true });

const STAT_ITEMS = [
  { label: 'Studios', target: 2000, prefix: '', suffix: '+' },
  { label: 'Albums Delivered', target: 50, prefix: '', suffix: 'k+' },
  { label: 'Payments Processed', target: 2, prefix: '₹', suffix: 'Cr+' },
  { label: 'Satisfaction Rate', target: 100, prefix: '', suffix: '%' },
];

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

const formatCounterValue = (value, item) => {
  const rounded = Math.round(value);
  const formattedNum = rounded.toLocaleString('en-IN');
  return `${item.prefix || ''}${formattedNum}${item.suffix || ''}`;
};

// ── Reference start pose (steep tilt, bottom-anchored pivot, so the phone
// looks like it's rising/standing up out of the ground as you scroll) ──────
const PHONE_START_POSE = {
  rotateX: 30,
  rotateY: -16,
  rotateZ: 3,
  skewX: 1,
  skewY: 0,
};

const PHONE_START_SCALE = 1.9;
const PHONE_START_Y_PUSH = 140; // extra downward push so bottom crops off-screen

const NewHeroSection = () => {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const phoneRef = useRef(null);
  const phoneGlowRef = useRef(null);
  const titleRef = useRef(null);
  const contentColRef = useRef(null);
  const qrRef = useRef(null);
  const descRef = useRef(null);
  const trustedTextRef = useRef(null);
  const buttonsRef = useRef(null);
  const trustedRef = useRef(null);
  const statsRef = useRef(null);
  const [statValues, setStatValues] = useState(STAT_ITEMS.map(() => 0));
  const [menuOpen, setMenuOpen] = useState(false);

  const handleScroll = (id) => {
    trackEvent('nav_click', { link_name: id });

    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleOpenFreeTrial = () => {
    trackEvent('start_free_trial_click', { location: 'hero_navbar' });
    window.dispatchEvent(new CustomEvent('open-free-trial-modal'));
  };

  const handleDownloadClick = (platform) => {
    trackEvent('app_download_click', { platform });
  };

  const [qrCodeUrl, setQrCodeUrl] = useState('');

  useLayoutEffect(() => {
    if (typeof window !== 'undefined') {
      setQrCodeUrl(`https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(window.location.origin + '/download')}`);
    }
  }, []);

  useLayoutEffect(() => {
    const syncStatsForSmallScreens = () => {
      if (window.innerWidth <= 900) {
        setStatValues(STAT_ITEMS.map(({ target }) => target));
      }
    };
    syncStatsForSmallScreens();
    window.addEventListener('resize', syncStatsForSmallScreens);
    return () => window.removeEventListener('resize', syncStatsForSmallScreens);
  }, []);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    const pin = pinRef.current;
    const phone = phoneRef.current;
    const phoneGlow = phoneGlowRef.current;
    const title = titleRef.current;
    const contentCol = contentColRef.current;
    const qr = qrRef.current;
    const desc = descRef.current;
    const buttons = buttonsRef.current;
    const trusted = trustedRef.current;
    const stats = statsRef.current;

    if (!section || !pin || !phone) return undefined;

    let animationFrameId;
    let isAnimatingCounters = false;
    let hasAnimatedCounters = false;

    const animateCounters = () => {
      if (isAnimatingCounters || hasAnimatedCounters) return;
      isAnimatingCounters = true;
      hasAnimatedCounters = true;
      setStatValues(STAT_ITEMS.map(() => 0));

      const duration = 1800;
      const startTime = performance.now();

      const tick = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutCubic(progress);
        setStatValues(STAT_ITEMS.map(({ target }) => Math.round(target * easedProgress)));
        if (progress < 1) {
          animationFrameId = requestAnimationFrame(tick);
        } else {
          isAnimatingCounters = false;
          setStatValues(STAT_ITEMS.map(({ target }) => target));
        }
      };
      animationFrameId = requestAnimationFrame(tick);
    };

    const mm = gsap.matchMedia();

    // ── DESKTOP ───────────────────────────────────────────────────────────────
    mm.add('(min-width: 901px)', () => {
      const contentItems = [qr, desc, buttons, trusted, trustedTextRef.current].filter(Boolean);

      gsap.set(contentCol, { visibility: 'visible' });
      gsap.set(contentItems, {
        opacity: 0, y: 42, x: 160, scale: 0.58,
        transformOrigin: 'left center',
      });
      gsap.set(phoneGlow, { opacity: 0, scale: 0.9 });
      gsap.set(stats, { opacity: 0, y: 24 });
      gsap.set(title, { opacity: 1, y: 0 });

      const getPhoneStartOffset = () => {
        const phoneCol = phone.closest('[data-phone-column]');
        if (!phoneCol) return { x: 0, y: 0 };

        const prevTransform = phone.style.transform;
        phone.style.transform = 'none';

        const stageRect = pin.getBoundingClientRect();
        const colRect = phoneCol.getBoundingClientRect();
        const phoneRect = phone.getBoundingClientRect();

        phone.style.transform = prevTransform;

        const stageCenterX = stageRect.left + stageRect.width / 2;
        const colCenterX = colRect.left + colRect.width / 2;
        const xOffset = stageCenterX - colCenterX;

        const viewportHeight = window.innerHeight;
        const phoneHeight = phoneRect.height || 700;

        let yOffset = 0;
        if (viewportHeight <= 950) {
          yOffset = Math.min(viewportHeight * 0.08, 72);
        } else {
          yOffset = viewportHeight - phoneHeight * 0.45 - phoneRect.top;
        }

        return { x: xOffset, y: yOffset };
      };

      const getPhoneEndOffset = () => ({ x: 0, y: 0 });

      // Fully hands the phone back to plain CSS layout — no leftover inline
      // transform at all — so it sits at its true natural position and stays
      // straight even if the layout reflows afterward (resize, content above
      // changing height, media query switch, etc).
      const releasePhoneToNaturalPosition = () => {
        gsap.set(phone, {
          clearProps: 'transform,x,y,rotateX,rotateY,rotateZ,skewX,skewY,scale',
          opacity: 1,
        });
      };

      // Only apply the tilted starting pose (and reveal the phone) once the
      // image has actually finished loading — otherwise getPhoneStartOffset()
      // reads a phoneRect.height of 0 and falls back to a wrong guess,
      // causing the phone to flash in the wrong spot on reload before
      // snapping to the correct position once the image + refresh land.
      const applyInitialPhonePose = () => {
        gsap.set(phone, {
          x: () => getPhoneStartOffset().x,
          y: () => getPhoneStartOffset().y + PHONE_START_Y_PUSH,
          rotateX: PHONE_START_POSE.rotateX,
          rotateY: PHONE_START_POSE.rotateY,
          rotateZ: PHONE_START_POSE.rotateZ,
          skewX: PHONE_START_POSE.skewX,
          skewY: PHONE_START_POSE.skewY,
          scale: PHONE_START_SCALE,
          transformOrigin: '50% 100%',
          opacity: 1,
        });
        ScrollTrigger.refresh();
      };

      if (phone.complete && phone.naturalWidth !== 0) {
        // Image was already cached/loaded (e.g. fast reload)
        applyInitialPhonePose();
      } else {
        phone.addEventListener('load', applyInitialPhonePose, { once: true });
      }

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: '+=140%',
          pin: pin,
          pinSpacing: true,
          scrub: 2,
          anticipatePin: 1,
          preventOverlaps: 'page-sections',
          fastScrollEnd: true,
          invalidateOnRefresh: true,
          onLeave: () => {
            releasePhoneToNaturalPosition();
            gsap.to(phoneGlow, {
              opacity: 1,
              scale: 1,
              duration: 0.55,
              delay: 0.22,
              ease: 'power2.out',
              overwrite: 'auto',
            });
            gsap.to(stats, {
              opacity: 1,
              y: 0,
              duration: 0.6,
              ease: 'power2.out',
              overwrite: 'auto',
              onStart: animateCounters,
            });
          },
          onEnterBack: () => {
            // Scrolling back up re-enters the pinned range. Set the phone
            // to its end pose so that scrubbing backwards from the end
            // state is perfectly smooth and seamless.
            gsap.set(phone, {
              x: getPhoneEndOffset().x,
              y: getPhoneEndOffset().y,
              rotateX: 0,
              rotateY: 0,
              rotateZ: 0,
              skewX: 0,
              skewY: 0,
              scale: 1,
              transformOrigin: '50% 100%',
              opacity: 1,
            });
            gsap.to(phoneGlow, { opacity: 0, scale: 0.9, duration: 0.4, overwrite: 'auto' });
            gsap.to(stats, { opacity: 0, y: 24, duration: 0.4, overwrite: 'auto' });
          },
          onRefresh: (self) => {
            if (self.progress >= 1) {
              releasePhoneToNaturalPosition();
              gsap.set(phoneGlow, { opacity: 1, scale: 1 });
              gsap.set(stats, { opacity: 1, y: 0 });
            }
          },
        },
      });

      tl.fromTo(phone,
        {
          x: () => getPhoneStartOffset().x,
          y: () => getPhoneStartOffset().y + PHONE_START_Y_PUSH,
          rotateX: PHONE_START_POSE.rotateX,
          rotateY: PHONE_START_POSE.rotateY,
          rotateZ: PHONE_START_POSE.rotateZ,
          skewX: PHONE_START_POSE.skewX,
          skewY: PHONE_START_POSE.skewY,
          scale: PHONE_START_SCALE,
          transformOrigin: '50% 100%',
        },
        {
          x: () => getPhoneEndOffset().x,
          y: () => getPhoneEndOffset().y,
          rotateX: 0,
          rotateY: 0,
          rotateZ: 0,
          skewX: 0,
          skewY: 0,
          scale: 1,
          duration: 1.25,
          ease: 'power2.inOut',
        },
        0
      )
        .to(contentItems, {
          opacity: 1, y: 0, x: 0, scale: 1,
          duration: 1.8, stagger: 0.14, ease: 'power2.out',
        }, 0.5);

      const refresh = () => ScrollTrigger.refresh();
      window.addEventListener('load', refresh);
      window.addEventListener('resize', refresh);

      return () => {
        window.removeEventListener('load', refresh);
        window.removeEventListener('resize', refresh);
        phone.removeEventListener('load', applyInitialPhonePose);
        if (animationFrameId) cancelAnimationFrame(animationFrameId);
        tl.scrollTrigger?.kill();
        tl.kill();
      };
    });

    // ── MOBILE — iOS Safari safe ──────────────────────────────────────────────
    mm.add('(max-width: 900px)', () => {
      // Show everything immediately on mobile — no pinning, no scrub.
      // Instead use simple ScrollTrigger-triggered (not scrubbed) animations.
      // This is the most reliable approach for iOS Safari.

      // Reset all to visible defaults first
      gsap.set([title, contentCol], { opacity: 1, y: 0, x: 0, scale: 1, clearProps: 'visibility' });
      gsap.set([qr, desc, buttons, trusted], { opacity: 0, y: 30 });
      gsap.set(stats, { opacity: 0, y: 20 });
      gsap.set(phoneGlow, { opacity: 0, scale: 0.9 });

      // Phone: starts angled (toned-down version of the reference pose,
      // bottom-anchored pivot), animates to flat/normal on enter. Only
      // applied + revealed once the image has actually loaded, to avoid
      // the flash-in-wrong-place issue on reload.
      const applyInitialMobilePose = () => {
        gsap.set(phone, {
          rotateX: 42,
          rotateY: -3,
          rotateZ: 2,
          skewX: 1,
          skewY: 0,
          scale: 1.22,
          transformOrigin: '50% 100%',
          y: 60,
          opacity: 1,
        });
        ScrollTrigger.refresh();
      };

      if (phone.complete && phone.naturalWidth !== 0) {
        applyInitialMobilePose();
      } else {
        phone.addEventListener('load', applyInitialMobilePose, { once: true });
      }

      // Phone settle animation — triggered, NOT scrubbed (iOS Safari safe)
      gsap.to(phone, {
        rotateX: 0,
        rotateY: 0,
        rotateZ: 0,
        skewX: 0,
        skewY: 0,
        scale: 1,
        y: 0,
        duration: 1.1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
          invalidateOnRefresh: true,
        },
      });

      // Glow appears after phone settles
      gsap.to(phoneGlow, {
        opacity: 1, scale: 1,
        duration: 0.8,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: phone,
          start: 'top 60%',
          toggleActions: 'play none none reverse',
          invalidateOnRefresh: true,
        },
      });

      // Content items stagger in
      gsap.to([qr, desc, buttons, trusted].filter(Boolean), {
        opacity: 1, y: 0,
        duration: 0.7,
        stagger: 0.1,
        ease: 'power2.out',
        scrollTrigger: {
          trigger: section,
          start: 'top 60%',
          toggleActions: 'play none none reverse',
          invalidateOnRefresh: true,
        },
      });

      // Stats animate in at bottom
      gsap.to(stats, {
        opacity: 1, y: 0,
        duration: 0.6,
        ease: 'power2.out',
        onStart: animateCounters,
        scrollTrigger: {
          trigger: stats,
          start: 'top 85%',
          toggleActions: 'play none none none',
          invalidateOnRefresh: true,
        },
      });

      const refresh = () => ScrollTrigger.refresh();
      window.addEventListener('load', refresh);
      window.addEventListener('resize', refresh);

      return () => {
        window.removeEventListener('load', refresh);
        window.removeEventListener('resize', refresh);
        phone.removeEventListener('load', applyInitialMobilePose);
        if (animationFrameId) cancelAnimationFrame(animationFrameId);
        ScrollTrigger.getAll().forEach((st) => {
          if (st.trigger === section || st.trigger === phone || st.trigger === stats) {
            st.kill();
          }
        });
      };
    });

    return () => mm.revert();
  }, []);

  return (
    <HeroWrapper ref={sectionRef} id="home">
      <PinStage ref={pinRef}>
        <TopNav>
          <BrandWrap>
            <BrandLogo src={KwikMemoriesLogo} alt="KwikMemories" />
          </BrandWrap>

          <NavRight>
            <NavPill>
              <NavLink active onClick={() => handleScroll('home')}>Home</NavLink>
              <NavLink onClick={() => handleScroll('features')}>Features</NavLink>
              <NavLink onClick={() => handleScroll('contactus')}>Contact us</NavLink>
              {/* <NavLink>Community</NavLink> */}
              <NavLink onClick={() => handleScroll('pricing')}>Pricing</NavLink>
              <NavLink onClick={() => handleScroll('testimonials')}>Testimonials</NavLink>
              {/* <NavCTAButton onClick={handleOpenFreeTrial}>Start Free Trial</NavCTAButton> */}
            </NavPill>
          </NavRight>

          <MobileMenuWrap>
            <MobileMenuButton
              type="button"
              onClick={() => setMenuOpen((prev) => !prev)}
              aria-label="Toggle navigation menu"
            >
              <HamburgerIcon $open={menuOpen}>
                <span />
                <span />
                <span />
              </HamburgerIcon>
            </MobileMenuButton>

            <MobileMenuOverlay $open={menuOpen}>
              <MobileMenuInner>
                <MobileMenuLinks>
                  <MobileMenuItem onClick={() => { setMenuOpen(false); handleScroll('home'); }}>Home</MobileMenuItem>
                  <MobileMenuItem onClick={() => { setMenuOpen(false); handleScroll('features'); }}>Features</MobileMenuItem>
                  <MobileMenuItem onClick={() => { setMenuOpen(false); handleScroll('contactus'); }}>Contact us</MobileMenuItem>
                  {/* <MobileMenuItem onClick={() => setMenuOpen(false)}>Community</MobileMenuItem> */}
                  <MobileMenuItem onClick={() => { setMenuOpen(false); handleScroll('pricing'); }}>Pricing</MobileMenuItem>
                  <MobileMenuItem onClick={() => { setMenuOpen(false); handleScroll('testimonials'); }}>Testimonials</MobileMenuItem>
                </MobileMenuLinks>
                {/* <MobileNavCTAButton onClick={() => { setMenuOpen(false); handleOpenFreeTrial(); }}>Start Free Trial</MobileNavCTAButton> */}
              </MobileMenuInner>
            </MobileMenuOverlay>
          </MobileMenuWrap>
        </TopNav>

        <Title ref={titleRef}>
          All-In-One <Highlight>AI Powered</Highlight>
          <br />
          Business Management Suite App
        </Title>

        <HeroContent>
          <PhoneColumn data-phone-column>
            <PhoneGlowWrap ref={phoneGlowRef}>
              <GlowCircleOuter />
              <GlowCircleInner />
            </PhoneGlowWrap>
            <PhoneImage ref={phoneRef} src={HeroPhone} alt="KwikMemories app" />
          </PhoneColumn>

          <ContentColumn ref={contentColRef}>
            <QrCard ref={qrRef}>
              <QrTitle>Scan to Download the App</QrTitle>
              <a href="/download" style={{ cursor: 'pointer', display: 'block' }}>
                <QrImage src={qrCodeUrl || HeroQr} alt="Download app QR" />
              </a>
            </QrCard>

            {/* <TrustedTextAcrossIndiaa ref={trustedTextRef}>Works on Android and iOS</TrustedTextAcrossIndiaa>

            <Description ref={descRef}>
              Kwik Memories puts your entire studio in your pocket. Manage leads, bookings, albums, payments, team members, and quotations — from one beautifully simple app, built for Indian photography studios.
            </Description> */}

            <ButtonsRow ref={buttonsRef}>
              <DownloadButton
                href="https://play.google.com/store/apps/details?id=com.kwikmedia&hl=en_IN"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleDownloadClick('android')}
              >
                Download on Android
                <IconCircle>
                  <ButtonIcon src={AndroidIcon} alt="Android" />
                </IconCircle>
              </DownloadButton>
              <DownloadButton
                href="https://apps.apple.com/in/app/kwikmemories/id6717602527"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => handleDownloadClick('ios')}
              >
                Download on iOS
                <IconCircle>
                  <ButtonIcon src={AppleIcon} alt="Apple" />
                </IconCircle>
              </DownloadButton>
            </ButtonsRow>

            <TrustedWrap ref={trustedRef}>
              <UsersStackRow>
                <UserAvatar src={user1} alt="User" />
                <UserAvatar src={user2} alt="User" />
                <UserAvatar src={user3} alt="User" />
                <UserAvatar src={user4} alt="User" />
                <UserAvatar src={user5} alt="User" />
              </UsersStackRow>
              <TrustedText>
                Trusted By: <UsersCount>2,000+ Studio Owners</UsersCount>
              </TrustedText>
              <TrustedTextAcrossIndia>Wedding, Portrait & Commercial Studios</TrustedTextAcrossIndia>
            </TrustedWrap>
          </ContentColumn>
        </HeroContent>

        <StatsRow ref={statsRef}>
          {STAT_ITEMS.map((item, index) => (
            <React.Fragment key={item.label}>
              <StatCard>
                <StatValue>{formatCounterValue(statValues[index], item)}</StatValue>
                <StatLabel>{item.label}</StatLabel>
              </StatCard>
              {index < STAT_ITEMS.length - 1 && <SeparatorCircle />}
            </React.Fragment>
          ))}
        </StatsRow>
      </PinStage>
    </HeroWrapper>
  );
};

export default NewHeroSection;

// ─── Styled Components ────────────────────────────────────────────────────────


const UsersStackRow = styled.div`
  display: flex;
  align-items: center;
`;

const UserAvatar = styled.img`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 2px solid #fff;
  object-fit: cover;
  margin-left: -10px;

  &:first-child {
    margin-left: 0;
  }
`;


const NavCTAButton = styled.button`
  background: linear-gradient(144.01deg, #ffc951 0%, #c94daa 100%);
  color: #fff;
  border: none;
  border-radius: 28px;
  padding: 10px 22px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileNavCTAButton = styled.button`
  background: linear-gradient(144.01deg, #ffc951 0%, #c94daa 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  margin-top: 12px;
`;

const HeroWrapper = styled.section`
  position: relative;
  isolation: isolate;
  z-index: 2;
  background: #ffffff;
  overflow-x: clip;

  @supports not (overflow: clip) {
    overflow-x: visible;
  }
`;

const PinStage = styled.div`
  position: relative;
  z-index: 1;
  min-height: 100vh;
  padding: 28px 64px 64px;
  display: flex;
  flex-direction: column;
  background: #ffffff;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background: linear-gradient(
      112.55deg,
      rgba(255, 201, 81, 0.2) 0%,
      rgba(201, 77, 170, 0.2) 62%
    );
    mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 0.78) 52%,
      rgba(0, 0, 0, 0.5) 68%,
      rgba(0, 0, 0, 0.22) 82%,
      rgba(0, 0, 0, 0) 100%
    );
    -webkit-mask-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 1) 0%,
      rgba(0, 0, 0, 0.78) 52%,
      rgba(0, 0, 0, 0.5) 68%,
      rgba(0, 0, 0, 0.22) 82%,
      rgba(0, 0, 0, 0) 100%
    );
  }

  > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 1024px) {
    padding: 24px 28px 40px;
  }

  @media (max-width: 900px) {
    padding: 20px 20px 36px;
    min-height: auto;
  }
`;

const TopNav = styled.div`
  position: relative;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 40px;

  @media (max-width: 1024px) {
    margin-bottom: 20px;
  }
`;

const NavRight = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  margin-left: auto;

  @media (max-width: 900px) {
    display: none;
  }
`;

const BrandWrap = styled.div`
  display: flex;
  align-items: center;
  margin-left: 16px;

  @media (max-width: 900px) {
    margin-left: 0;
  }
`;

const BrandLogo = styled.img`
  width: 88px;
  height: 88px;
  object-fit: contain;

  @media (max-width: 900px) {
    width: 72px;
    height: 72px;
  }
`;

const NavPill = styled.div`
  background: #ffffff;
  border-radius: 999px;
  padding: 7px 30px;
  display: flex;
  align-items: center;
  gap: 42px;

  @media (max-width: 900px) {
    display: none;
  }
`;

const NavLink = styled.span`
  cursor: pointer;
  font-size: 16px;
  font-family: 'Gilroy-Bold', 'Poppins', sans-serif;
  font-weight: 500;
  color: ${({ active }) => (active ? '#0d0d0d' : '#0c0b0b')};
  background: ${({ active }) =>
    active ? 'linear-gradient(144.01deg, #FFC951 0%, #C94DAA 100%)' : 'none'};
  -webkit-background-clip: ${({ active }) => (active ? 'text' : 'border-box')};
  -webkit-text-fill-color: ${({ active }) => (active ? 'transparent' : '#121212')};
  background-clip: ${({ active }) => (active ? 'text' : 'border-box')};

  @media (max-width: 1024px) {
    font-size: 18px;
  }
`;

// ─── Mobile Menu ──────────────────────────────────────────────────────────────

const MobileMenuWrap = styled.div`
  display: none;
  position: relative;
  z-index: 100;

  @media (max-width: 900px) {
    display: block;
  }
`;

const MobileMenuButton = styled.button`
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  position: relative;
  z-index: 110;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
`;

const HamburgerIcon = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 5px;
  width: 22px;
  height: 22px;
  transform: ${({ $open }) => ($open ? 'scale(1.2)' : 'scale(1)')};
  transform-origin: center;
  transition: transform 0.3s ease;

  span {
    display: block;
    width: 22px;
    height: 2px;
    border-radius: 2px;
    background: #121212;
    transform-origin: center;
    transition: transform 0.3s ease, opacity 0.3s ease, width 0.3s ease;

    &:nth-child(1) {
      width: ${({ $open }) => ($open ? '16px' : '11px')};
      align-self: ${({ $open }) => ($open ? 'center' : 'flex-start')};
      transform: ${({ $open }) => ($open ? 'translateY(7px) rotate(45deg)' : 'none')};
    }
    &:nth-child(2) {
      opacity: ${({ $open }) => ($open ? '0' : '1')};
      width: ${({ $open }) => ($open ? '0' : '22px')};
    }
    &:nth-child(3) {
      width: ${({ $open }) => ($open ? '16px' : '11px')};
      align-self: ${({ $open }) => ($open ? 'center' : 'flex-end')};
      transform: ${({ $open }) => ($open ? 'translateY(-7px) rotate(-45deg)' : 'none')};
    }
  }
`;

const MobileMenuOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 90;
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 0 0 24px 24px;
  transform: ${({ $open }) => ($open ? 'translateY(0)' : 'translateY(-100%)')};
  opacity: ${({ $open }) => ($open ? '1' : '0')};
  pointer-events: ${({ $open }) => ($open ? 'all' : 'none')};
  transition: transform 0.42s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.3s ease;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.08);
  padding-top: env(safe-area-inset-top, 0px);
`;

const MobileMenuInner = styled.div`
  padding: 100px 28px 40px;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

const MobileMenuLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const MobileMenuItem = styled.button`
  border: none;
  background: transparent;
  text-align: left;
  width: 100%;
  padding: 16px 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  color: #111;
  font-family: 'Gilroy-Bold', 'Poppins', sans-serif;
  font-size: 19px;
  font-weight: 700;
  cursor: pointer;
  transition: padding-left 0.2s ease;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    padding-left: 16px;
    background: linear-gradient(144.01deg, #ffc951 0%, #c94daa 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  &:nth-child(1) {
    background: linear-gradient(144.01deg, #FFC951 0%, #C94DAA 40%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-weight: 700;
  }
`;

// ─── Hero Layout ──────────────────────────────────────────────────────────────

const HeroContent = styled.div`
  background: transparent;
  border-radius: 28px;
  padding: 36px 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 64px;
  flex: 1;

  @media (max-width: 1024px) {
    padding: 28px 20px 30px;
    gap: 22px;
  }

  @media (min-width: 768px) and (max-width: 900px) {
    padding: 24px 16px 28px;
    gap: 18px;
  }

  @media (max-width: 767px) {
    flex-direction: column;
    padding: 4px 8px 20px;
    gap: 0;
  }
`;

const PhoneColumn = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44%;
  min-height: 580px;
  transform-style: preserve-3d;
  perspective: 1200px;
  overflow: visible;
  flex-shrink: 0;

  @media (max-width: 1024px) {
    width: 46%;
    min-height: 500px;
  }

  @media (min-width: 901px) and (max-width: 1800px) {
    margin-top: 48px;
  }

  @media (min-width: 768px) and (max-width: 900px) {
    width: 44%;
    min-height: 420px;
  }

  @media (max-width: 767px) {
    width: 100%;
    min-height: 120px;
    margin-top: 6px;
    margin-bottom: 22px;
    perspective: none;
    transform-style: flat;
  }
`;

const PhoneGlowWrap = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 550px;
  height: 550px;
  z-index: 1;
  pointer-events: none;

  @media (max-width: 1024px) {
    width: 440px;
    height: 440px;
  }

  @media (min-width: 768px) and (max-width: 900px) {
    width: 320px;
    height: 320px;
  }

  @media (max-width: 767px) {
    width: 260px;
    height: 260px;
  }
`;

const GlowCircleOuter = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: linear-gradient(140deg, #ffc951 0%, #c94daa 100%);
  opacity: 0.15;
`;

const GlowCircleInner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 470px;
  height: 470px;
  border-radius: 50%;
  background: linear-gradient(140deg, #ffc951 0%, #c94daa 100%);
  opacity: 0.25;

  @media (max-width: 1024px) {
    width: 380px;
    height: 380px;
  }

  @media (min-width: 768px) and (max-width: 900px) {
    width: 280px;
    height: 280px;
  }

  @media (max-width: 767px) {
    width: 220px;
    height: 220px;
  }
`;

const PhoneImage = styled.img`
  position: relative;
  width: min(380px, 92%);
  height: auto;
  z-index: 2;
  will-change: transform;
  transform-style: preserve-3d;
  pointer-events: none;
  opacity: 0; /* hidden until JS confirms image is loaded + correctly positioned */

  @media (max-width: 1024px) {
    width: min(330px, 90%);
  }

  @media (min-width: 768px) and (max-width: 900px) {
    width: min(220px, 32vw);
  }

  @media (max-width: 767px) {
    width: min(190px, 64vw);
    transform-style: flat;
    will-change: auto;
  }
`;

const ContentColumn = styled.div`
  position: relative;
  width: 56%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  overflow: visible;
  z-index: 3;
  flex-shrink: 0;
  min-width: 0;

  @media (max-width: 1024px) and (min-width: 901px) {
    width: 54%;
    padding-top: 28px;
  }

  @media (min-width: 768px) and (max-width: 900px) {
    width: 56%;
    padding-top: 38px;
  }

  @media (max-width: 767px) {
    width: 100%;
    align-items: center;
    text-align: center;
  }
`;

const Title = styled.h1`
  margin: 8px 0 18px;
  font-family: 'Playfair Display', serif;
  font-weight: 600;
  font-style: italic;
  font-size: 64px;
  line-height: 100%;
  text-align: center;
  color: #121212;

  @media (max-width: 1024px) {
    font-size: clamp(56px, 7.2vw, 76px);
  }

  @media (max-width: 900px) {
    font-size: clamp(40px, 11vw, 56px);
    line-height: 1.04;
    margin: 2px 0 12px;
  }
`;

const Highlight = styled.span`
  position: relative;
  display: inline-block;
  padding: 6px 28px 12px;
  line-height: 1.08;
  vertical-align: baseline;
  color: #ffffff;
  z-index: 1;
  margin-left: 14px;

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 0;
    right: 0;
    bottom: 2px;
    background: linear-gradient(144.01deg, #ffc951 0%, #c94daa 120%);
    transform: skewX(-25deg);
    z-index: -1;
  }

  @media (max-width: 900px) {
    padding: 5px 16px 8px;
    margin-left: 8px;
  }
`;

const QrCard = styled.div`
  margin-top: 22px;
  background: rgba(255, 255, 255, 0.35);
  border: 1px solid rgba(255, 255, 255, 1);
  border-radius: 18px;
  padding: 14px 6px 14px;
  width: 248px;
  min-height: 304px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 1024px) {
    margin-top: 10px;
    width: 220px;
    min-height: 282px;
  }

  @media (max-width: 900px) {
    margin-top: 0;
    width: min(250px, 84vw);
    min-height: auto;
    padding: 14px 10px 12px;
  }
`;

const QrTitle = styled.p`
  margin: 0 0 8px;
  width: 228px;
  box-sizing: border-box;
  font-family: 'Playfair Display', serif;
  font-weight: 600;
  font-size: 28px;
  line-height: 125%;
  text-align: center;
  color: rgba(0, 0, 0, 1);

  @media (max-width: 1024px) {
    width: 200px;
    font-size: 22px;
  }

  @media (max-width: 900px) {
    width: 100%;
    font-size: 22px;
  }
`;

const QrImage = styled.img`
  width: 184px;
  height: 184px;
  display: block;
  border-radius: 10px;
  max-width: 184px;

  @media (max-width: 1024px) {
    width: 168px;
    height: 168px;
    max-width: 168px;
  }

  @media (max-width: 900px) {
    width: 164px;
    height: 164px;
    max-width: 164px;
  }
`;

const Description = styled.p`
  margin: 18px 0 0;
  max-width: 560px;
  font-family: 'Gilroy-Medium', 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 130%;
  color: #2d2d2d;

  @media (max-width: 1024px) {
    font-size: 16px;
  }

  @media (max-width: 900px) {
    margin-top: 14px;
    font-size: 16px;
    line-height: 1.4;
    max-width: 92vw;
  }
`;

const TrustedTextAcrossIndia = styled.p`
  margin: 6px 0 0;
  max-width: 560px;
  font-family: 'Gilroy-Medium', 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 18px;
  line-height: 130%;
  color: #2d2d2d;
`;

const TrustedTextAcrossIndiaa = styled.p`
  margin: 20px 0 0;
  max-width: 560px;
  font-family: 'Gilroy-Medium', 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 130%;
  color: #2d2d2d;
`;

const ButtonsRow = styled.div`
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;

  @media (max-width: 900px) {
    width: 100%;
    margin-top: 16px;
    gap: 10px;
  }
`;

const DownloadButton = styled.a`
  background: #ffffff;
  border: none;
  border-radius: 999px;
  padding: 20px 24px;
  min-height: 74px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-size: 23px;
  font-family: 'Gilroy-SemiBold', 'Poppins', sans-serif;
  font-weight: 500;
  line-height: 100%;
  color: #101010;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;

  @media (max-width: 1024px) {
    font-size: 18px;
    min-height: 62px;
    padding: 14px 18px;
  }

  @media (max-width: 900px) {
    min-height: 54px;
    font-size: 17px;
    padding: 10px 14px;
    justify-content: center;
  }

  @media (max-width: 430px) {
    width: 100%;
  }
`;

const IconCircle = styled.span`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(140.02deg, #ffc951 2.22%, #c94daa 96.12%);
  flex-shrink: 0;
`;

const ButtonIcon = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
`;

const TrustedWrap = styled.div`
  margin-top: 18px;

  @media (max-width: 900px) {
    margin-top: 14px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
`;

const UsersStack = styled.img`
  width: 190px;
  height: auto;
  display: block;

  @media (max-width: 900px) {
    width: 170px;
  }
`;

const TrustedText = styled.p`
  margin: 10px 0 0;
  font-size: 30px;
  line-height: 100%;
  font-family: 'Gilroy-Medium', 'Poppins', sans-serif;
  font-weight: 400;
  color: #121212;

  @media (max-width: 1024px) {
    font-size: 24px;
  }

  @media (max-width: 900px) {
    font-size: 24px;
    text-align: center;
  }
`;

const UsersCount = styled.span`
  font-family: 'Playfair Display', serif;
  font-weight: 800;
  font-size: 35px;
  line-height: 100%;
  background: linear-gradient(140.02deg, #ffc951 2.22%, #c94daa 96.12%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;

  @media (max-width: 1024px) {
    font-size: 30px;
  }

  @media (max-width: 900px) {
    font-size: 30px;
  }
`;

// ─── Stats ────────────────────────────────────────────────────────────────────

const StatsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 76px;
  margin-top: 64px;
  width: 100%;
  flex-wrap: nowrap;

  @media (max-width: 1024px) {
    gap: 18px;
    margin-top: 36px;
    justify-content: space-between;
  }

  @media (min-width: 1000px) and (max-width: 1400px) {
    gap: 28px;
    margin-top: 44px;
  }

  @media (max-width: 900px) {
    display: grid;
    grid-template-columns: repeat(2, minmax(132px, 1fr));
    justify-content: center;
    margin-left: auto;
    margin-right: auto;
    gap: 14px 20px;
    margin-top: 28px;
    max-width: 420px;
    width: min(100%, 420px);
  }
`;

const StatCard = styled.div`
  background: linear-gradient(
    140.02deg,
    rgba(255, 201, 81, 0.1) 2.22%,
    rgba(201, 77, 170, 0.1) 96.12%
  );
  border: none;
  border-radius: 24px;
  width: 184px;
  padding: 24px;
  height: 164px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    width: 132px;
    height: 132px;
    border-radius: 18px;
  }

  @media (max-width: 900px) {
    width: 100%;
    max-width: 190px;
    justify-self: center;
  }

  @media (max-width: 1024px) and (min-width: 901px) {
    width: 150px;
    height: 138px;
    border-radius: 20px;
  }

  @media (min-width: 1000px) and (max-width: 1400px) {
    width: 170px;
    height: 152px;
    border-radius: 22px;
  }
`;

const StatValue = styled.span`
  font-family: 'Gilroy-Bold', 'Poppins', sans-serif;
  font-size: 42px;
  font-weight: 700;
  background: linear-gradient(140.02deg, #ffc951 2.22%, #c94daa 96.12%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;

  @media (max-width: 768px) {
    font-size: 32px;
  }

  @media (max-width: 1024px) and (min-width: 901px) {
    font-size: 34px;
  }

  @media (min-width: 1000px) and (max-width: 1400px) {
    font-size: 38px;
  }
`;

const StatLabel = styled.span`
  font-family: 'Playfair Display', serif;
  font-size: 24px;
  text-align: center;
  font-weight: 300;
  color: #3d3b3b;
  margin-top: 4px;

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 1024px) and (min-width: 901px) {
    font-size: 18px;
  }

  @media (min-width: 1000px) and (max-width: 1400px) {
    font-size: 20px;
  }
`;

const SeparatorCircle = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(140.02deg, #ffc951 2.22%, #c94daa 96.12%);
  flex-shrink: 0;

  @media (max-width: 900px) {
    display: none;
  }

  @media (max-width: 1024px) and (min-width: 901px) {
    width: 18px;
    height: 18px;
  }

  @media (min-width: 1000px) and (max-width: 1400px) {
    width: 20px;
    height: 20px;
  }
`;